/**
 * Section Presence Domain
 * Tracks which game section each teammate is currently viewing (e.g., "globe", "fleet", "hq").
 * Also tracks which city a teammate has focused (city panel open) on the globe.
 * Section presence is always on for every player: nobody subscribes to it.
 * When a player navigates to a different section, the change is sent to the server and broadcast to teammates.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getViewerUserId } from './current-team';
import { handleTeammateCursorGone } from './section-cursor';
import { handleTeammateHoversGone } from './cosign';
import { OpCode } from '../constants/op-codes';
import { matchConnection, sendToMatch } from '../services/nakama/match-send';

// ==================== TYPES ====================

/**
 * The game's four sections, in the rail's order. The server accepts exactly
 * these (validSections in section-presence.go).
 */
export const GAME_SECTIONS = ['boardroom', 'globe', 'fleet', 'innovation'] as const;
export type GameSection = (typeof GAME_SECTIONS)[number];

/** The key that names each section in the player's language: the rail, a teammate's card, a page that failed. */
export const SECTION_WORDS: Record<GameSection, string> = {
  boardroom: 'sections.boardroom',
  globe: 'sections.globe',
  fleet: 'sections.fleet',
  innovation: 'sections.innovation',
};

/** Whether a section name from the server is one of the four. */
export function isGameSection(value: string | undefined): value is GameSection {
  return (GAME_SECTIONS as readonly string[]).includes(value ?? '');
}

// ==================== STORE ====================

interface SectionPresenceState {
  /** Map of user_id → section name for all teammates */
  teamSections: Record<string, string>;

  /**
   * Map of user_id → city_id for teammates with a city panel open on the globe
   * (sparse: no entry is no city; city 0, Honolulu, is a city like any other)
   */
  teamCityFocus: Record<string, number>;

  /** Replace all sections (full snapshot on join) */
  setTeamSections: (sections: Record<string, string>) => void;

  /** Replace all city focus entries (full snapshot on join) */
  setTeamCityFocus: (cityFocus: Record<string, number>) => void;

  /** Update a single player's section (delta on change) */
  updatePlayerSection: (userId: string, section: string) => void;

  /** Update a single player's city focus (null = no city) */
  updatePlayerCityFocus: (userId: string, cityId: number | null) => void;

  /** Remove a player (on leave) */
  removePlayer: (userId: string) => void;

  /** Reset to initial state */
  reset: () => void;
}

const initialState = {
  teamSections: {} as Record<string, string>,
  teamCityFocus: {} as Record<string, number>,
};

/**
 * Section Presence Store
 * Manages which section each teammate is viewing and which city they are focused on
 */
export const useSectionPresenceStore = create<SectionPresenceState>()(
  devtools(
    (set) => ({
      ...initialState,

      setTeamSections: (sections) => {
        set({ teamSections: sections }, false, 'setTeamSections');
      },

      setTeamCityFocus: (cityFocus) => {
        set({ teamCityFocus: cityFocus }, false, 'setTeamCityFocus');
      },

      updatePlayerSection: (userId, section) => {
        set(
          (state) => {
            const newCityFocus = { ...state.teamCityFocus };
            // If player left the globe, clear their city focus
            if (section !== 'globe') {
              delete newCityFocus[userId];
            }
            return {
              teamSections: {
                ...state.teamSections,
                [userId]: section,
              },
              teamCityFocus: newCityFocus,
            };
          },
          false,
          'updatePlayerSection'
        );
      },

      updatePlayerCityFocus: (userId, cityId) => {
        set(
          (state) => {
            const newCityFocus = { ...state.teamCityFocus };
            if (cityId !== null) {
              newCityFocus[userId] = cityId;
            } else {
              delete newCityFocus[userId];
            }
            return { teamCityFocus: newCityFocus };
          },
          false,
          'updatePlayerCityFocus'
        );
      },

      removePlayer: (userId) => {
        set(
          (state) => {
            const newSections = { ...state.teamSections };
            const newCityFocus = { ...state.teamCityFocus };
            delete newSections[userId];
            delete newCityFocus[userId];
            return { teamSections: newSections, teamCityFocus: newCityFocus };
          },
          false,
          'removePlayer'
        );
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    { name: 'section-presence-store' }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle OpCodeSectionPresenceState message
 * Full snapshot of teammate sections and city focus, sent to newly joined player
 */
export function handleSectionPresenceState(data: {
  type: string;
  sections: Record<string, string>;
  city_focus?: Record<string, number>;
}) {
  console.log('[SectionPresence] Received presence state:', Object.keys(data.sections).length, 'entries');
  useSectionPresenceStore.getState().setTeamSections(data.sections);
  if (data.city_focus) {
    useSectionPresenceStore.getState().setTeamCityFocus(data.city_focus);
  }
}

/**
 * Handle OpCodeSectionPresenceUpdate message
 * Single player section change, broadcast from server
 */
export function handleSectionPresenceUpdate(data: {
  type: string;
  user_id: string;
  section: string;
  /** The city they have open; null (or absent) is none — 0 is Honolulu. */
  city_id?: number | null;
}) {
  console.log('[SectionPresence] Player', data.user_id, 'moved to', data.section, 'city_id:', data.city_id ?? 'none');

  // Read previous section before updating store
  const prevSection = useSectionPresenceStore.getState().teamSections[data.user_id];
  const sectionChanged = prevSection !== data.section;

  useSectionPresenceStore.getState().updatePlayerSection(data.user_id, data.section);

  // Update city focus
  const cityId = data.city_id ?? null;
  useSectionPresenceStore.getState().updatePlayerCityFocus(data.user_id, cityId);

  // When a teammate gains city focus (same section), clear their cursor (cursor → city avatar handoff)
  if (cityId !== null) {
    handleTeammateCursorGone(data.user_id);
  }

  // When a teammate actually changes section, remove their cursor and co-sign state
  // (they're no longer in our section, so their hover is stale)
  if (sectionChanged) {
    handleTeammateCursorGone(data.user_id);
    console.log('[SectionPresence] Removing cosign state for', data.user_id.slice(0, 8), 'who moved to', data.section);
    handleTeammateHoversGone(data.user_id);
  }
}

/**
 * A player joined (players.ts): their badge shows in the section they are
 * in — the globe, until they say otherwise — before the server's next word.
 */
export function handlePlayerArrived(userId: string, section: string): void {
  useSectionPresenceStore.getState().updatePlayerSection(userId, section);
}

/** A player left the match (players.ts): their section and their city go. */
export function handlePlayerGone(userId: string): void {
  useSectionPresenceStore.getState().removePlayer(userId);
}

// ==================== SERVICE METHODS ====================

/**
 * Sends a section change to the server.
 * Call when the player navigates to a different game section.
 * Also optimistically updates the local store so the current user's badge updates immediately
 * (the server excludes the sender from the broadcast).
 */
export async function sendSectionChange(section: GameSection): Promise<void> {
  if (!matchConnection()) {
    return;
  }

  // Optimistic local update — server won't echo back to sender
  const currentUserId = getViewerUserId();
  if (currentUserId) {
    useSectionPresenceStore.getState().updatePlayerSection(currentUserId, section);
  }

  const message = {
    section,
  };

  await sendToMatch(OpCode.OpCodeSectionPresenceSubscribe, message, 'a section change');
}

/**
 * Sends a city focus change within the globe section.
 * Call when the player opens or closes a city panel on the globe.
 * @param cityId - The city ID being focused, or null to clear focus (0 is Honolulu)
 */
export async function sendCityFocusChange(cityId: number | null): Promise<void> {
  if (!matchConnection()) {
    return;
  }

  // Optimistic local update
  const currentUserId = getViewerUserId();
  if (currentUserId) {
    useSectionPresenceStore.getState().updatePlayerCityFocus(currentUserId, cityId);
  }

  const message = {
    section: 'globe',
    city_id: cityId,
  };

  await sendToMatch(OpCode.OpCodeSectionPresenceSubscribe, message, 'a city focus change');
}
